/**
 * CSS injected into the widget's shadow root. Reproduces OwnID's real OTP-form
 * (the `drawer`/`otp-input` module) as captured from the live SDK, and themes
 * off the same `--ownid-*` CSS custom properties OwnID reads (with OwnID's own
 * default values as fallbacks). The mock does NOT reference app-specific
 * (`--sync-*`) variables — the consumer (sync/pay) overrides it exactly as it
 * would the real widget.
 *
 * IMPORTANT: this string is mounted as the FIRST child (<style>) of the shadow
 * root. The app's style-customisation code appends rules to
 * `ownid-root.shadowRoot.children[0].innerHTML`, so <style> must stay first.
 */
export const widgetStyles = `
:host {
  all: initial;
  display: block;
  visibility: visible;
  font-family: var(--ownid-font-family, 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif);
}
* { box-sizing: border-box; }

.ownid-widget {
  visibility: visible;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  text-align: center;
  color: var(--ownid-color-text-primary, #0b0b0b);
}

/* "Welcome back" loader — OwnID .spinner-module (track + rotating dashed arc) */
.ownid-widget__loader-row {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 24px 0 0;
  height: 42px;
}
.ownid-widget__spinner {
  position: relative;
  width: 30px;
  height: 30px;
}
.ownid-widget__spinner svg {
  position: absolute;
  inset: 0;
  width: 30px;
  height: 30px;
}
.ownid-widget__spinner circle {
  fill: none;
  stroke-width: 8;
}
.ownid-widget__spinner-bg {
  stroke: var(--ownid-spinner-bg-color, #e6e8ec);
}
.ownid-widget__spinner-arc {
  stroke: var(--ownid-spinner-color, #0b0b0b);
  stroke-linecap: round;
  animation: ownid-mock-dash 2s cubic-bezier(0.61, 0.24, 0.44, 0.79) infinite;
}
.ownid-widget__spinner-arc-svg {
  transform-origin: center;
  animation: ownid-mock-rotate 2s cubic-bezier(0.61, 0.24, 0.44, 0.79) infinite;
}
@keyframes ownid-mock-rotate {
  100% { transform: rotate(720deg); }
}
@keyframes ownid-mock-dash {
  0%   { stroke-dasharray: 1 270; stroke-dashoffset: 70; }
  50%  { stroke-dasharray: 80 270; stroke-dashoffset: 220; }
  100% { stroke-dasharray: 1 270; stroke-dashoffset: 70; }
}

/* Title — OwnID .drawer_title */
.ownid-widget__title {
  margin: 24px 0 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 44px;
  text-align: center;
  color: var(--ownid-flow-title-color, #0b0b0b);
  font-family: var(--ownid-flow-title-font-family, inherit);
}

/* Login-id row + pill — OwnID .drawer_login_id / .username mainButton */
.ownid-widget__login-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 24px 0 0;
}
.ownid-widget__pill {
  display: inline-flex;
  align-items: center;
  padding: 7px 9px;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: #fff;
}
.ownid-widget__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  min-width: 26px;
  flex: none;
  border-radius: 50%;
  background: var(--ownid-color-primary, #3e68dd);
  color: #fff;
}
.ownid-widget__avatar svg {
  width: 12px;
  height: 13px;
  display: block;
  fill: currentColor;
}
.ownid-widget__phone {
  margin: 0 4px 0 8px;
  font-size: 16px;
  font-weight: 400;
  line-height: 23px;
  color: #0b0b0b;
}

/* Description — OwnID .drawer_description */
.ownid-widget__description {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 24px 0 0;
  min-height: 46px;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.2px;
  text-align: center;
  color: var(--ownid-color-text-primary, #0b0b0b);
}

/* OTP boxes — OwnID .otp-input-module. Class names kept so the app's existing
 * shadow-DOM overrides ([class*="otp-input-module__input"] input) still apply. */
.ownid-widget__otp { margin: 24px 0 0; }
.otp-input-module__form {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 0;
}
.otp-input-module__input { display: block; }
.otp-input-module__input input {
  width: 64px;
  height: 68px;
  padding: 0;
  text-align: center;
  font-size: 32px;
  font-weight: 400;
  line-height: 44px;
  color: #111112;
  background: var(--ownid-input-bg, #fff);
  border: 1px solid #dedde1;
  border-radius: 6px;
  outline: none;
  -moz-appearance: textfield;
}
.otp-input-module__input input::-webkit-outer-spin-button,
.otp-input-module__input input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.otp-input-module__input input:focus {
  border-color: var(--ownid-color-primary, #3e68dd);
}
.ownid-widget.is-error .otp-input-module__input input {
  border-color: #d92d20;
}
.ownid-widget.is-verifying .otp-input-module__input input {
  opacity: 0.6;
}

.ownid-widget__error {
  min-height: 18px;
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: #d92d20;
}

/* Resend — OwnID text/primary button */
.ownid-widget__resend-row {
  display: flex;
  align-items: center;
  justify-content: center;
}
.ownid-widget__resend {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  margin: 24px 0 0;
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  color: var(--ownid-color-primary, #3e68dd);
}
.ownid-widget__resend:disabled {
  opacity: 0.5;
  cursor: default;
}
`
